'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Checkbox, Field, FieldLabel } from '@nwwp/ui-shadcn'
import { funnelSchema } from '~/lib/schemas/funnel-schema'
import { LINKS } from '~/utils/links'
import { useFunnelStore } from '../../_hooks/use-funnel-store'
import type { FunnelStepComponent } from '../../types'
import { FunnelStep } from '../FunnelStep'

const schema = funnelSchema.pick({ email: true }).extend({ consent: z.literal(true) })
type Schema = z.infer<typeof schema>

export const Email: FunnelStepComponent = ({ onSubmit }) => {
  const email = useFunnelStore.getState().data.email
  const { control, formState, handleSubmit } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: { email: email ?? '', consent: false as true },
    mode: 'onChange',
  })

  const beforeSubmit = ({ email }: Schema) => onSubmit({ email })

  return (
    <FunnelStep onSubmit={handleSubmit(beforeSubmit)}>
      <FunnelStep.Title>Please provide your email address</FunnelStep.Title>
      <FunnelStep.TextInput control={control} name="email" placeholder="your@email.com" autoFocus />
      <Controller
        name="consent"
        control={control}
        render={({ field, fieldState }) => (
          <Field orientation="horizontal" data-invalid={fieldState.invalid} className="max-w-4xl items-start gap-0">
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-invalid={fieldState.invalid}
              className="mr-3 size-[18px] rounded-[2px] border-2 border-green-lighter"
            />
            <div className="flex flex-col gap-1">
              <FieldLabel className="block [font-size:16px] font-normal text-white">
                By proceeding, you acknowledge and agree to NerdWallet&rsquo;s{' '}
                <a
                  href={LINKS.TERMS_OF_USE}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold underline"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href={LINKS.PRIVACY_POLICY}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold underline"
                >
                  Privacy Policy
                </a>
                . You confirm that you have received NerdWallet Wealth Partners, LLC&rsquo;s{' '}
                <a href={LINKS.ADV_2A} target="_blank" rel="noreferrer noopener" className="font-semibold underline">
                  ADV Part 2A
                </a>{' '}
                and{' '}
                <a href={LINKS.FORM_CRS} target="_blank" rel="noreferrer noopener" className="font-semibold underline">
                  Form CRS
                </a>
                , which are provided through the included links. You also agree to receive emails from NerdWallet Wealth
                Partners, LLC, and you may unsubscribe at any time{' '}
                <a
                  href={LINKS.EMAIL_UNSUBSCRIBE}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-semibold underline"
                >
                  here
                </a>
                .
              </FieldLabel>
            </div>
          </Field>
        )}
      />
      <FunnelStep.Button disabled={!formState.isValid}>Next</FunnelStep.Button>
    </FunnelStep>
  )
}

Email.path = 'email'
Email.schema = schema
